import { useRef, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { FileDown, FileSpreadsheet } from "lucide-react";
import { useReportDashboardStats } from "@/hooks/useReportDashboardStats";
import { format } from "date-fns";
import { ReportDashboardView } from "@/components/reports/ReportDashboardView";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

const Reports = () => {
  const [showResults, setShowResults] = useState(false);
  const [dashboardDate, setDashboardDate] = useState<string>(format(new Date(), "yyyy-MM-dd"));
  const [dashboardTime, setDashboardTime] = useState<string>("00:00");
  const [dashboardStartDateTime, setDashboardStartDateTime] = useState<Date | null>(null);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const reportRef = useRef<HTMLDivElement | null>(null);

  const { data: dashboardData, isLoading: dashboardLoading } = useReportDashboardStats(dashboardStartDateTime);

  const handleGenerateReport = () => {
    const [year, month, day] = dashboardDate.split("-").map(Number);
    const [hours, minutes] = dashboardTime.split(":").map(Number);
    const startDateTime = new Date(year, month - 1, day, hours, minutes);
    setDashboardStartDateTime(startDateTime);
    setShowResults(true);
  };

  const handleDownloadCSV = () => {
    if (!dashboardData) return;
    
    const lines: string[] = [];
    lines.push("Dashboard Summary Report");
    lines.push(`Period: ${dashboardDate} ${dashboardTime} + 24 hours`);
    lines.push("");
    lines.push("Key Metrics");
    lines.push(`Total Discharges (Completed),${dashboardData.stats.totalDischarges}`);
    lines.push(`Discharges Initiated,${dashboardData.stats.initiatedDischarges}`);
    lines.push(`Avg Discharge Time,${dashboardData.stats.avgDischargeTime}`);
    lines.push(`Pending Discharges,${dashboardData.stats.pendingDischarges}`);
    lines.push(`Payment Pending,${dashboardData.stats.paymentPending}`);
    lines.push(`Patients Marked for Discharge,${dashboardData.extended.patientsMarked}`);
    lines.push(`Planned Discharge,${dashboardData.extended.plannedDischarge}`);
    lines.push(`LOS Carry-Over,${dashboardData.extended.losCarryOver}`);
    lines.push(`Escalations L1,${dashboardData.extended.escalationsL1}`);
    lines.push(`Escalations L2,${dashboardData.extended.escalationsL2}`);
    lines.push(`Escalations L3,${dashboardData.extended.escalationsL3}`);
    lines.push(`Patients Admitted in Period,${dashboardData.extended.admittedInPeriod}`);
    lines.push("");
    lines.push("Stepwise TAT Performance");
    lines.push("Step,Avg Minutes,Within TAT %,L1 Breach %,L2 Breach %,L3 Breach %,Total");
    dashboardData.stepwiseTAT.forEach(s => {
      lines.push(`${s.name},${s.avgMinutes.toFixed(2)},${s.withinTAT},${s.level1Breach},${s.level2Breach},${s.level3Breach},${s.totalCompleted}`);
    });
    lines.push("");
    lines.push("Ward-wise TAT Performance");
    lines.push("Ward,Avg Minutes,Within TAT %,L1 Breach %,L2 Breach %,L3 Breach %,Total");
    dashboardData.wardwiseTAT.forEach(w => {
      lines.push(`${w.ward},${w.avgMinutes.toFixed(2)},${w.withinTAT},${w.level1Breach},${w.level2Breach},${w.level3Breach},${w.totalCompleted}`);
    });
    lines.push("");
    lines.push("Patient Discharge Details (24-hour cycle)");
    lines.push("S.No,Patient Name,UHID/Apex ID,Discharge Initiated at,Total Elapsed time,Physical Discharge at,Discharge status,Payment Status");
    dashboardData.patientRows.forEach((row, index) => {
      lines.push(`${index + 1},${row.patientName},${row.apexId},${row.dischargeInitiatedAt},${row.totalElapsedTime},${row.physicalDischargeAt},${row.dischargeStatus},${row.paymentStatus}`);
    });
    
    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dashboard-summary-${dashboardDate}.csv`;
    a.click();
  };

  const handleDownloadPDF = async () => {
    if (!dashboardData || !dashboardStartDateTime || !reportRef.current || isExportingPDF) return;

    setIsExportingPDF(true);
    try {
      const exportScale = 1.25;
      const imageQuality = 0.72;
      const canvas = await html2canvas(reportRef.current, {
        scale: exportScale,
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollY: -window.scrollY,
        ignoreElements: (element) => element.getAttribute("data-export-ignore") === "true",
      });
      const imgData = canvas.toDataURL("image/jpeg", imageQuality);
      const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4", compress: true });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight, undefined, "FAST");
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight, undefined, "FAST");
        heightLeft -= pageHeight;
      }

      pdf.save(`dashboard-summary-${dashboardDate}.pdf`);
    } finally {
      setIsExportingPDF(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-[1400px] mx-auto px-6 py-8">
        {/* Report Selection */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Discharge Summary (24-Hour Report)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dashboard-date">Select Date</Label>
                <Input
                  id="dashboard-date"
                  type="date"
                  value={dashboardDate}
                  onChange={(e) => setDashboardDate(e.target.value)}
                  className="w-full"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dashboard-time">Select Time</Label>
                <Input
                  id="dashboard-time"
                  type="time"
                  value={dashboardTime}
                  onChange={(e) => setDashboardTime(e.target.value)}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">Report will show data for 24 hours from this time</p>
              </div>
            </div>

            <Button 
              onClick={handleGenerateReport} 
              disabled={dashboardLoading}
              className="w-full md:w-auto"
            >
              {dashboardLoading ? "Generating..." : "Generate Discharge Summary"}
            </Button>
          </CardContent>
        </Card>

        {/* Dashboard Summary Report */}
        {showResults && dashboardStartDateTime && (
            <div ref={reportRef} className="space-y-6 rounded-lg bg-background p-4">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">Dashboard Summary Report</h2>
                <div className="flex gap-2" data-export-ignore="true">
                  <Button variant="outline" size="sm" onClick={handleDownloadCSV}>
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleDownloadPDF} disabled={isExportingPDF}>
                    <FileDown className="w-4 h-4 mr-2" />
                    {isExportingPDF ? "Exporting..." : "Export PDF"}
                  </Button>
                </div>
              </div>

              <ReportDashboardView 
                data={dashboardData || null} 
                isLoading={dashboardLoading}
                startDateTime={dashboardStartDateTime}
              />
            </div>
        )}
      </main>
    </div>
  );
};

export default Reports;
